import { EquipmentTypeScopeBadge } from '@/components/badges/EquipmentTypeScopeBadge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { EquipmentTypeDisplay } from '@/nodes/NodeUI';
import { useDiscardEquipmentType } from '@/db/useDiscardEquipmentType';
import { equipmentTypeEditState, equipmentTypeState } from '@/state';
import { useAsyncAtomValue, useLoadableSetAtom } from '@/state/loadableHooks';
import logger from '@/util/logger';
import type { EquipmentType, EquipmentTypeId } from '@rock/types';
import { RoomGroupsById, RoomTypeLabels } from '@rock/types';
import { type FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { EquipmentTypeStagingToolbar } from './EquipmentTypeStagingToolbar';
import EquipmentTypeForm from './EquipmentTypeForm';
import { RetireEquipmentTypeDialog } from './RetireEquipmentTypeDialog';

const log = logger('EquipmentTypeDetails');

const EquipmentTypeDetails: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => {
    const [equipmentTypeLoadable, updateEquipmentType] = useLoadableSetAtom(equipmentTypeEditState(typeId));
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { busy: discarding, discardEquipmentType } = useDiscardEquipmentType();
    const equipmentType = equipmentTypeLoadable.state === 'hasData' ? equipmentTypeLoadable.data : undefined;

    if (!equipmentType) return null;

    return (
        <div className="space-y-2">
            <EquipmentTypeStagingToolbar typeId={typeId} />
            {!editing ? (
                <DisplayEquipmentType type={equipmentType} startEditing={() => setEditing(true)} />
            ) : (
                <EquipmentTypeForm
                    spaceId={equipmentType.spaceId}
                    type={equipmentType}
                    loading={loading || discarding}
                    onSave={async (type) => {
                        setLoading(true);
                        try {
                            await updateEquipmentType(type);
                            setEditing(false);
                            toast.success('Equipment Type Updated');
                            log.info(`Updated Equipment Type: ${type.id}`);
                        } finally {
                            setLoading(false);
                        }
                    }}
                    onDelete={async () => {
                        if (await discardEquipmentType(equipmentType.id)) {
                            navigate(`/equipment/types/catalog?space=${equipmentType.spaceId}`);
                        }
                    }}
                    onRetired={() => setEditing(false)}
                    onCancel={() => setEditing(false)}
                />
            )}
        </div>
    );
};

const DisplayEquipmentType: FC<{ type: EquipmentType; startEditing: () => void }> = ({ type, startEditing }) => {
    const typeId = type.id;

    return (
        <div className="space-y-2">
            <div className="flex flex-row justify-end gap-2">
                <RetireButton typeId={typeId} />
                <Button onClick={startEditing} variant="outline" size="sm" className="cursor-pointer">
                    Edit
                </Button>
            </div>
            <div className="space-y-4">
                <div>
                    <Label>ID</Label>
                    <div className="text-gray-500">
                        <div className="inline-block">
                            <EquipmentTypeDisplay spaceId={type.spaceId} typeId={typeId} />
                        </div>
                    </div>
                </div>
                <div>
                    <Label>Type</Label>
                    <div>
                        <EquipmentTypeScopeBadge scope={type.scope} />
                    </div>
                </div>
                {type.scope === 'system' && (
                    <div>
                        <Label>System Parts</Label>
                        <div className="text-gray-500">
                            <div className="flex flex-col items-center p-2 gap-1">
                                <div className="flex flex-row flex-wrap gap-1">
                                    {type.systemParts?.map((p) => (
                                        <EquipmentTypeDisplay key={p.typeId} spaceId={type.spaceId} typeId={p.typeId} />
                                    ))}
                                    {!type.systemParts?.length ? 'None' : ''}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div>
                    <Label>Title</Label>
                    <div className="text-gray-500">{type.title}</div>
                </div>
                <div>
                    <Label>Short Title</Label>
                    <div className="text-gray-500">{type.shortTitle}</div>
                </div>
                <div>
                    <Label>Subtitle</Label>
                    <div className="text-gray-500">{type.subtitle ?? '-'}</div>
                </div>
                <div>
                    <Label>Group</Label>
                    <div className="text-gray-500">{type.group}</div>
                </div>
                <div>
                    <Label>Order</Label>
                    <div className="text-gray-500">{type.order}</div>
                </div>
                {type.roomGroupHint && (
                    <div>
                        <Label>Assumed Room Group</Label>
                        <div className="text-gray-500">{RoomGroupsById[type.roomGroupHint]?.label}</div>
                    </div>
                )}
                {type.autoSelectRoomType && (
                    <div>
                        <Label>Auto-Select Room Type</Label>
                        <div className="text-gray-500">{RoomTypeLabels[type.autoSelectRoomType]}</div>
                    </div>
                )}
                <div>
                    <Label>Images</Label>
                    <div className="flex flex-row flex-wrap gap-1">
                        {type.images?.map((image) => (
                            <img
                                key={image.id}
                                src={image.thumbnail}
                                alt="Thumbnail"
                                className="w-[100px] h-[100px] object-cover rounded-md border"
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

/** Offered only for a published type that's still in the catalog. */
const RetireButton: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => {
    const live = useAsyncAtomValue(equipmentTypeState(typeId), undefined);
    const [open, setOpen] = useState(false);
    // The retire stamps live before it moves anything, so an open dialog outlives `retiredAt` until the call returns.
    if (!live || (live.retiredAt && !open)) return null;
    return (
        <>
            <Button onClick={() => setOpen(true)} variant="outline" size="sm" className="cursor-pointer">
                Retire
            </Button>
            <RetireEquipmentTypeDialog type={live} open={open} onOpenChange={setOpen} />
        </>
    );
};

export default EquipmentTypeDetails;
